/**
 * A notebook's sources, grouped by the collection each belongs to.
 *
 * ALWAYS grouped, with no special case for "the whole corpus": attaching all of Pustaka
 * shows every collection as its own collapsed heading rather than a single row. Decided
 * 19 Aug 2026, and it makes the panel uniform — one rule, and a collection that appears in
 * the corpus later appears here, which follows from the corpus being live.
 *
 * Collapsed by default, because a collection can hold 1,257 documents and the four a reader
 * cares about must not be buried. COLLAPSING IS A VIEW STATE AND NOTHING ELSE — a collapsed
 * group is still searched. A collapsed group that stopped being searched would be a scope
 * change disguised as a UI preference, which is the sort of thing nobody finds for weeks.
 *
 * A document belongs to exactly ONE collection (`documents.collectionId` is single-valued),
 * so the grouping is well defined and no document appears twice. The 239 documents that
 * belong to none get their own heading, named for what is true — they are unfiled, not
 * "other".
 */
import * as Collapsible from "@radix-ui/react-collapsible";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/primitives.tsx";
import { api } from "@/lib/api.ts";
import { formatCount } from "@/i18n/index.ts";
import type { Collection, CorpusDocument, NotebookSourceIssue } from "@/lib/api-types.ts";
import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";

export interface SourceGroup {
  /** The collection id, or `null` for documents that belong to none. */
  collectionId: string | null;
  name: string;
  /** Documents from this group that are IN the notebook. */
  documents: CorpusDocument[];
  /** True when the whole collection was added, rather than documents picked from it. */
  whole: boolean;
  /** How many documents the collection holds in total — only meaningful when `whole`. */
  totalDocuments: number;
  /** Documents from this group the reader has excluded. */
  excludedCount: number;
}

/**
 * Arrange a notebook's sources into groups.
 *
 * Pure, so the arrangement can be tested without rendering: the interesting cases are a
 * collection added WHOLE (one heading, a count, no rows until expanded) versus documents
 * picked individually out of several collections (a heading each, only the picked rows).
 */
export function groupSources({
  collections,
  documents,
  wholeCollectionIds,
  excludedDocumentIds,
  unfiledLabel,
}: {
  collections: Collection[];
  documents: CorpusDocument[];
  wholeCollectionIds: string[];
  excludedDocumentIds: string[];
  unfiledLabel: string;
}): SourceGroup[] {
  const byId = new Map(collections.map((collection) => [collection.id, collection]));
  const excluded = new Set(excludedDocumentIds);
  const groups = new Map<string | null, SourceGroup>();

  const ensure = (collectionId: string | null): SourceGroup => {
    const existing = groups.get(collectionId);
    if (existing) return existing;
    const collection = collectionId ? byId.get(collectionId) : undefined;
    const group: SourceGroup = {
      collectionId,
      name: collection?.name ?? (collectionId ? collectionId : unfiledLabel),
      documents: [],
      whole: false,
      totalDocuments: collection?.document_count ?? 0,
      excludedCount: 0,
    };
    groups.set(collectionId, group);
    return group;
  };

  // Whole collections first, so they lead the panel in the order they were added — the
  // reader's own order, not the alphabet's.
  for (const collectionId of wholeCollectionIds) {
    ensure(collectionId).whole = true;
  }
  for (const document of documents) {
    const group = ensure(document.collection_id);
    if (excluded.has(document.id)) {
      group.excludedCount += 1;
      continue;
    }
    // A document picked individually out of a collection that is ALSO added whole is
    // already covered by the collection; listing it again would double-count the source.
    if (!group.whole) group.documents.push(document);
  }
  return [...groups.values()];
}

export function SourcesPanel({
  collections,
  documents,
  wholeCorpus,
  wholeCollectionIds,
  excludedDocumentIds,
  corpusTotal,
  issues,
  onAddSources,
  onDetachWholeCorpus,
  onRemoveCollection,
  onRemoveDocument,
  onToggleExcluded,
}: {
  collections: Collection[];
  documents: CorpusDocument[];
  wholeCorpus: boolean;
  wholeCollectionIds: string[];
  excludedDocumentIds: string[];
  corpusTotal: number;
  issues: NotebookSourceIssue[];
  /** Opens the picker. Sources are only ever ADDED from there — this panel shows what is
   *  in the notebook, never the world of what could be. */
  onAddSources: () => void;
  onDetachWholeCorpus: () => void;
  onRemoveCollection: (collectionId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  /** Tick a document off INSIDE a whole collection. Not a removal — the collection stays,
   *  keeps tracking new arrivals, and this one document is excluded from it. */
  onToggleExcluded: (documentId: string) => void;
}) {
  const { t, i18n } = useTranslation();

  // Attaching all of Pustaka shows EVERY collection as a heading, so the panel looks the
  // same whether the reader added one collection or the whole corpus.
  const effectiveWholeIds = useMemo(
    () => (wholeCorpus ? collections.map((collection) => collection.id) : wholeCollectionIds),
    [wholeCorpus, collections, wholeCollectionIds],
  );

  const groups = useMemo(
    () =>
      groupSources({
        collections,
        documents,
        wholeCollectionIds: effectiveWholeIds,
        excludedDocumentIds,
        unfiledLabel: t("notebook.unfiled"),
      }),
    [collections, documents, effectiveWholeIds, excludedDocumentIds, t],
  );

  const moved = issues.filter((issue) => issue.kind === "moved").length;
  const missing = issues.filter((issue) => issue.kind === "missing").length;

  return (
    <div className="sources-panel">
      <div className="sources-head">
        <span className="eyebrow">{t("notebook.sources")}</span>
        <Button size="sm" variant="primary" onClick={onAddSources}>
          {t("notebook.addSources")}
        </Button>
      </div>

      {/* Handled and lost are DIFFERENT facts and get different words. Collapsing them
          into one number would make a followed document read as a loss. */}
      {moved > 0 ? <p className="sources-note">{t("notebook.moved", { count: moved })}</p> : null}
      {missing > 0 ? (
        <p className="sources-note is-warn">{t("notebook.missing", { count: missing })}</p>
      ) : null}

      {wholeCorpus ? (
        <div className="source-whole">
          <span>{t("notebook.wholePustaka", { total: formatCount(corpusTotal, i18n.language) })}</span>
          <button type="button" className="source-item-remove" onClick={onDetachWholeCorpus}>
            ×
          </button>
        </div>
      ) : null}

      {groups.length === 0 && !wholeCorpus ? (
        <p className="sources-empty">{t("notebook.empty")}</p>
      ) : (
        groups.map((group) => (
          <SourceGroupRow
            key={group.collectionId ?? "__unfiled__"}
            group={group}
            wholeCorpus={wholeCorpus}
            excludedDocumentIds={excludedDocumentIds}
            onRemoveCollection={onRemoveCollection}
            onRemoveDocument={onRemoveDocument}
            onToggleExcluded={onToggleExcluded}
          />
        ))
      )}
    </div>
  );
}

function SourceGroupRow({
  group,
  wholeCorpus,
  excludedDocumentIds,
  onRemoveCollection,
  onRemoveDocument,
  onToggleExcluded,
}: {
  group: SourceGroup;
  wholeCorpus: boolean;
  excludedDocumentIds: string[];
  onRemoveCollection: (collectionId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  onToggleExcluded: (documentId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  // A group added whole starts collapsed — it can hold over a thousand documents. A group
  // of hand-picked documents starts open, because the reader chose each one.
  const [open, setOpen] = useState(!group.whole);

  // The collection's ACTUAL documents, fetched only when opened. A whole collection is
  // live, so this is what it holds right now rather than a stored list — and it is what
  // makes per-document ticking possible without turning the collection into a snapshot.
  const contents = useQuery({
    queryKey: ["corpus", "collection-contents", group.collectionId],
    queryFn: () => api.corpus({ collection_id: group.collectionId as string, limit: 200 }),
    enabled: open && group.whole && Boolean(group.collectionId),
  });

  const excluded = new Set(excludedDocumentIds);
  const count = group.whole ? group.totalDocuments : group.documents.length;
  const rows = group.whole ? (contents.data?.documents ?? []) : group.documents;

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="source-group">
      <div className="source-group-head">
        <Collapsible.Trigger asChild>
          <button type="button" className="source-group-toggle">
            <span className="source-group-caret" aria-hidden="true">
              {open ? "▾" : "▸"}
            </span>
            <span className="source-group-name">{group.name}</span>
            <span className="source-group-count">{formatCount(count, i18n.language)}</span>
          </button>
        </Collapsible.Trigger>
        {/* A quiet × rather than a labelled button and a pill. Three controls competing on
            one narrow row squeezed the collection's NAME down to "Ga…", which is the one
            thing the row exists to show. Removing all of Pustaka is one action at the top,
            so a collection inside it has no × of its own. */}
        {group.collectionId && !wholeCorpus ? (
          <button
            type="button"
            className="source-remove"
            aria-label={t("notebook.removeCollection", { name: group.name })}
            title={t("notebook.removeCollection", { name: group.name })}
            onClick={() => onRemoveCollection(group.collectionId as string)}
          >
            ×
          </button>
        ) : null}
      </div>

      <Collapsible.Content>
        {group.whole ? (
          <p className="source-group-live">{t("notebook.liveCollection")}</p>
        ) : null}
        {group.whole && contents.isPending ? (
          <p className="source-group-live">{t("common.loading")}</p>
        ) : null}
        <ul className="source-list">
          {rows.map((document) => {
            const { label, subject, full } = parseLegalTitle(document.title);
            const isOff = excluded.has(document.id);
            return (
              <li key={document.id} className="source-item" title={full}>
                {/* Inside a whole collection a document is TICKED OFF, not removed: it
                    arrived with the collection and was never listed on its own, so an
                    exclusion is the only thing that can express "not this one" while the
                    collection keeps tracking new arrivals. */}
                {group.whole ? (
                  <label className="source-item-tick">
                    <input
                      type="checkbox"
                      checked={!isOff}
                      onChange={() => onToggleExcluded(document.id)}
                    />
                    <span className={`source-item-text${isOff ? " is-off" : ""}`}>
                      {label ? <span className="mono source-item-label">{label}</span> : null}
                      <span>{truncateAtWord(subject, 64)}</span>
                    </span>
                  </label>
                ) : (
                  <>
                    <span className="source-item-text">
                      {label ? <span className="mono source-item-label">{label}</span> : null}
                      <span>{truncateAtWord(subject, 64)}</span>
                    </span>
                    <button
                      type="button"
                      className="source-remove"
                      aria-label={t("notebook.removeDocument", { title: full })}
                      title={t("notebook.removeDocument", { title: full })}
                      onClick={() => onRemoveDocument(document.id)}
                    >
                      ×
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {/* Says what is NOT shown, so a 1,257-document collection listing 200 does not
            read as "this is all of it". */}
        {group.whole && contents.data && contents.data.total > rows.length ? (
          <p className="source-group-live">
            {t("notebook.moreInCollection", {
              shown: formatCount(rows.length, i18n.language),
              total: formatCount(contents.data.total, i18n.language),
            })}
          </p>
        ) : null}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
